/**
 * EditableParagraphs — Lesemodus-Absätze mit „an Ort und Stelle
 * bearbeiten" (Alex 2026-09-30: „kann man das auch so machen, dass der
 * Read-Modus editierbar ist?").
 *
 * Der Lesemodus rendert Markdown zu HTML; der Edit-Modus zeigt die rohen
 * CRDT-Absätze in TipTap. Ein vollständiger WYSIWYG-Editor über dem CRDT
 * würde die wortgenaue Autorenschaft (Zeichenpositionen im Markdown-
 * Quelltext) verschieben. Darum derselbe Weg wie in der Canvas-Ansicht:
 * jeder Absatz bleibt gerendert, trägt aber `data-block-id` +
 * `data-paragraph-index`; Doppelklick oder der Stift am linken Rand öffnet
 * den CanvasCardEditor-Popover genau für diesen Absatz. Der Popover ist
 * ein vollwertiger CollabEditor (Yjs, Claims, Pushes, Authorship), auf
 * einen Absatz eingeblendet.
 *
 * Fenced Code-Blöcke (mermaid, d2, …) liegen als mehrere CRDT-Absätze vor
 * (\n\n-Split). Sie werden gebündelt gerendert und bekommen keinen Stift —
 * dort bleibt der Edit-Modus der richtige Ort.
 */

import type { MouseEvent as ReactMouseEvent } from 'react'
import { injectAnnotationMark } from '../utils/annotationMarks'
import { blockIdFor, buildParagraphAtoms, type EditableParagraph } from '../utils/paragraphAtoms'

export type { EditableParagraph }

export interface ParagraphEditTarget {
  /** Server-gestempelte block_id oder `_paragraph_<index>` als Fallback
   *  (CanvasCardEditor kennt beide Formen). */
  blockId: string
  paragraphIndex: number
  /** Das gerenderte Element — für die Popover-Position. */
  element: HTMLElement
}

export type ParagraphEditHandler = (target: ParagraphEditTarget) => void

/** Anker einer Anmerkung: Absatz (block_id) + angemerkte Passage. */
export interface AnnotationAnchor {
  id: number | string
  blockId: string
  /** Zeichenposition der Passage im Absatz, falls bekannt. */
  anchorStart?: number
  quote: string
  /** Farbe des Autors für die Markierung im Text. */
  color?: string | null
  /** Markierung ohne Notiz: gepunktete Linie. */
  kind?: 'note' | 'highlight'
}

export function EditableParagraphs({
  paragraphs,
  renderMarkdown,
  onParagraphEdit,
  editingBlockId,
  annotationAnchors,
}: {
  paragraphs: EditableParagraph[]
  renderMarkdown: (md: string) => string
  /** Fehlt der Handler, wird nur gerendert — kein Stift, kein Doppelklick. */
  onParagraphEdit?: ParagraphEditHandler
  /** Der gerade im Popover bearbeitete Absatz (visuelle Markierung). */
  editingBlockId?: string | null
  /** Anmerkungen, deren Passage in diesen Absätzen markiert wird. */
  annotationAnchors?: AnnotationAnchor[]
}) {
  const atoms = buildParagraphAtoms(paragraphs)
  const renderWithMarks = (blockId: string, md: string): string => {
    let html = renderMarkdown(md)
    if (annotationAnchors) {
      for (const a of annotationAnchors) {
        if (a.blockId === blockId) {
          const before = typeof a.anchorStart === 'number' ? md.slice(Math.max(0, a.anchorStart - 400), a.anchorStart) : undefined
          html = injectAnnotationMark(html, a.quote, a.id, a.color, a.kind ?? 'note', before)
        }
      }
    }
    return html
  }
  return (
    <>
      {atoms.map((atom, ai) => {
        if (atom.kind === 'codeblock') {
          return (
            <div
              key={`cb-${ai}`}
              className="read-para read-para--codeblock"
              data-block-id={blockIdFor(atom.paragraphs[0])}
              data-paragraph-index={atom.paragraphs[0].index}
              dangerouslySetInnerHTML={{
                __html: renderMarkdown(atom.paragraphs.map((p) => p.text).join('\n\n')),
              }}
            />
          )
        }
        const p = atom.p
        const blockId = blockIdFor(p)
        const editing = !!editingBlockId && editingBlockId === blockId
        const open = (e: ReactMouseEvent<HTMLElement>) => {
          if (!onParagraphEdit) return
          // Eine bestehende Textauswahl (Anmerken) nicht überfahren.
          const sel = window.getSelection()
          if (e.type === 'dblclick' && sel && !sel.isCollapsed && sel.toString().trim().length > 0) {
            // Doppelklick selektiert ein Wort — das ist kein Anmerken-Wunsch.
            sel.removeAllRanges()
          }
          e.preventDefault()
          onParagraphEdit({ blockId, paragraphIndex: p.index, element: e.currentTarget.closest('.read-para') as HTMLElement })
        }
        return (
          <div
            key={`p-${ai}`}
            className={`read-para${onParagraphEdit ? ' read-para--editable' : ''}${editing ? ' read-para--editing' : ''}`}
            data-block-id={blockId}
            data-paragraph-index={p.index}
            onDoubleClick={onParagraphEdit ? open : undefined}
          >
            {onParagraphEdit && (
              <button
                type="button"
                className="read-para__edit"
                title="Absatz bearbeiten (Doppelklick)"
                aria-label="Absatz bearbeiten"
                onClick={open}
              >
                ✎
              </button>
            )}
            <div
              className="read-para__body"
              dangerouslySetInnerHTML={{ __html: renderWithMarks(blockId, p.text || '') }}
            />
          </div>
        )
      })}
    </>
  )
}
